import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Typography,
  Box,
  Link,
  Card,
  CardContent,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import MoreIcon from '@mui/icons-material/More';
import Avatar from '@mui/material/Avatar';
import VisibilityIcon from "@mui/icons-material/Visibility";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CloseIcon from "@mui/icons-material/Close";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import configServ from "../../services/config";

const EmployeeTable = ({
  employeeList,
  loader,
  page,
  rowsPerPage,
  total,
  setPage,
  setRowsPerPage,
  handleEdit,
  handleDelete,
  handleOpenCamera,
  handleOpenMoreDetails,
  handleOpenEmployeeDetails,
  showTitle = true,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { company_id } = useSelector((state) => state.GlobalVariables);

  const [subordinateDialogOpen, setSubordinateDialogOpen] = useState(false);
  const [subordinateList, setSubordinateList] = useState([]);
  const [subordinateLoader, setSubordinateLoader] = useState(false);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleViewSites = (emp) => {
    Swal.fire({
      title: "Assigned Sites",
      html:
        emp.site_names && emp.site_names.length > 0
          ? `<ul style="text-align:left; padding-left:20px;">
            ${emp.site_names
            .map((site) => `<li>${site}</li>`)
            .join("")}
          </ul>`
          : "<p>No sites assigned.</p>",
      icon: "info",
      confirmButtonText: "Close",
    });
  };

  const HandleGetsubordinate = async (value) => {
    setSubordinateLoader(true);
    setSubordinateDialogOpen(true);
    setSubordinateList([]);
    try {
      const res = await configServ.getEmployeeHierarchy({
        company_id,
        page: 1,
        per_page: 100,
        supervisor_id: value,
      });
      if (res?.status && Array.isArray(res?.data)) {
        setSubordinateList(res.data);
      } else if (res?.data && Array.isArray(res.data.data)) {
        setSubordinateList(res.data.data);
      } else {
        setSubordinateList([]);
      }
    } catch (e) {
      console.error("Error fetching subordinates:", e);
      setSubordinateList([]);
    } finally {
      setSubordinateLoader(false);
    }
  };

  const data = employeeList || [];

  if (loader) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", height: 300, alignItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, width: "100%" }}>
      {showTitle && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
          Employee List
        </Typography>
      )}

      {/* Desktop View */}
      {!isMobile ? (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976d2" }}>
                {["S.No", "Code", "Name", "Designation", "Department", "Address", "Mobile", "Status", "Actions"].map((h) => (
                  <TableCell key={h} sx={{ color: "white", fontWeight: "bold" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {data.length > 0 ? (
                data.map((emp, index) => (
                  <TableRow key={emp.id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{emp.employee_code || "-"}</TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Zoom>
                          <Avatar
                            src={emp.image_url}
                            alt={emp.contact_name || emp.fname}
                            sx={{ width: 32, height: 32, cursor: "pointer" }}
                          />
                        </Zoom>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() => handleOpenEmployeeDetails?.(emp)}
                          sx={{ font: "inherit", fontWeight: 600, textAlign: "left" }}
                        >
                          {emp.contact_name || `${emp.fname} ${emp.lname || ""}`.trim() || "-"}
                        </Link>
                      </div>
                    </TableCell>
                    <TableCell>{emp.designation_name || "-"}</TableCell>
                    <TableCell>{emp.department_name || "-"}</TableCell>
                    <TableCell>{emp.address || "-"}</TableCell>
                    <TableCell>{emp.mobile || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ color: emp.status ? "green" : "red", fontWeight: "bold" }}>
                        {emp.status ? "ACTIVE" : "INACTIVE"}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(emp)}
                          sx={{ backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" } }}
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(emp.id)}
                          sx={{ backgroundColor: "error.main", color: "white", "&:hover": { backgroundColor: "error.dark" } }}
                          title="Deactivate"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCamera(emp)}
                          sx={{ backgroundColor: "error.main", color: "white", "&:hover": { backgroundColor: "error.dark" } }}
                          title="Deactivate"
                        >
                          <AddAPhotoIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenMoreDetails(emp)}
                          sx={{ backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" } }}
                          title="View Details"
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                        {
                          emp.supervisor === 1 && (<IconButton
                            size="small"
                            onClick={() => HandleGetsubordinate(emp.id)}
                            sx={{ backgroundColor: "info.main", color: "white", "&:hover": { backgroundColor: "info.dark" } }}
                            title="Subordinates"
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>)
                        }


                        {/* <IconButton
                          size="small"
                          onClick={() => handleViewSites(emp)}
                          sx={{
                            backgroundColor: "primary.main",
                            color: "white",
                            "&:hover": { backgroundColor: "primary.dark" },
                          }}
                          title="View Sites"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton> */}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">No Data Found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        /* Mobile View */
        <Box>
          {data.length > 0 ? (
            data.map((emp, index) => (
              <Card key={emp.id} sx={{ mb: 2, borderLeft: "5px solid #1976d2", boxShadow: 2 }}>
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                      #{page * rowsPerPage + index + 1}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: emp.status ? "green" : "red", fontWeight: "bold" }}
                    >
                      {emp.status ? "ACTIVE" : "INACTIVE"}
                    </Typography>
                  </Box>


                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Zoom>
                      <Avatar
                        src={emp.image_url}
                        alt={emp.contact_name || emp.fname}
                        sx={{ width: 32, height: 32, cursor: "pointer" }}
                      />
                    </Zoom>
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => handleOpenEmployeeDetails?.(emp)}
                      sx={{ font: "inherit", fontWeight: "bold", textAlign: "left" }}
                    >
                      {emp.contact_name || `${emp.fname} ${emp.lname || ""}`.trim()}
                    </Link>
                  </div>

                  {emp.employee_code && (
                    <Typography variant="body2" color="text.secondary">
                      Code: {emp.employee_code}
                    </Typography>
                  )}
                  {emp.designation_name && (
                    <Typography variant="body2">Designation: {emp.designation_name}</Typography>
                  )}
                  {emp.department_name && (
                    <Typography variant="body2">Department: {emp.department_name}</Typography>
                  )}
                  {emp.address && (
                    <Typography variant="body2">Address: {emp.address}</Typography>
                  )}
                  <Typography variant="body2">Mobile: {emp.mobile || "-"}</Typography>

                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(emp)}
                      sx={{ backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(emp.id)}
                      sx={{ backgroundColor: "error.main", color: "white", "&:hover": { backgroundColor: "error.dark" } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenCamera(emp)}
                      sx={{ backgroundColor: "error.main", color: "white", "&:hover": { backgroundColor: "error.dark" } }}
                    >
                      <AddAPhotoIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenMoreDetails(emp)}
                      sx={{ backgroundColor: "primary.main", color: "white", "&:hover": { backgroundColor: "primary.dark" } }}
                      title="View Details"
                    >
                      <MoreIcon fontSize="small" />
                    </IconButton>
                    {
                      emp.supervisor === 1 && (<IconButton
                        size="small"
                        onClick={() => HandleGetsubordinate(emp.id)}
                        sx={{ backgroundColor: "info.main", color: "white", "&:hover": { backgroundColor: "info.dark" } }}
                        title="Subordinates"
                      >
                        <LinkOffIcon fontSize="small" />
                      </IconButton>)
                    }
                    {/* <IconButton
                      size="small"
                      onClick={() => handleViewSites(emp)}
                      sx={{
                        backgroundColor: "primary.main",
                        color: "white",
                        "&:hover": { backgroundColor: "primary.dark" },
                      }}
                      title="View Sites"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton> */}
                  </Stack>
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography align="center">No Data Found</Typography>
          )}
        </Box>
      )}

      {/* Pagination */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[50, 100, 150]}
        sx={{ mt: 2 }}
      />

      <Dialog
        open={subordinateDialogOpen}
        onClose={() => setSubordinateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#1976d2",
            color: "white",
          }}
        >
          <Typography variant="h6" component="div" sx={{ fontWeight: "bold" }}>
            Subordinate Details
          </Typography>
          <IconButton
            aria-label="close"
            onClick={() => setSubordinateDialogOpen(false)}
            sx={{
              color: "white",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: isMobile ? 1.5 : 2 }}>
          {subordinateLoader ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading subordinates...
              </Typography>
            </Box>
          ) : subordinateList.length > 0 ? (
            isMobile ? (
              /* --- MOBILE VIEW (CARDS) --- */
              <Grid container spacing={1.5}>
                {subordinateList.map((item, index) => (
                  <Grid item xs={12} key={item.subordinate_id || index}>
                    <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 1 }}>
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              #{index + 1} | Code: {item.subordinate_code || "-"}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", lineHeight: 1.2 }}>
                              {item.subordinate_name || "-"}
                            </Typography>
                          </Box>

                          {/* Action Buttons */}
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCamera(item)}
                              sx={{
                                backgroundColor: "error.main",
                                color: "white",
                                "&:hover": { backgroundColor: "error.dark" },
                              }}
                            >
                              <AddAPhotoIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenMoreDetails(item)}
                              sx={{
                                backgroundColor: "primary.main",
                                color: "white",
                                "&:hover": { backgroundColor: "primary.dark" },
                              }}
                              title="View Details"
                            >
                              <MoreIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Box>

                        {/* Details List */}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          <strong>Designation:</strong> {item.subordinate_designation || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Department:</strong> {item.subordinate_department || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Mobile:</strong> {item.subordinate_mobile || "-"}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              /* --- DESKTOP VIEW (TABLE) --- */
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["S.No", "Code", "Name", "Designation", "Department", "Mobile", "Action"].map(
                        (header) => (
                          <TableCell
                            key={header}
                            sx={{
                              backgroundColor: "#f5f5f5",
                              fontWeight: "bold",
                              color: "#333",
                            }}
                          >
                            {header}
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {subordinateList.map((item, index) => (
                      <TableRow key={item.subordinate_id || index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.subordinate_code || "-"}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {item.subordinate_name || "-"}
                        </TableCell>
                        <TableCell>{item.subordinate_designation || "-"}</TableCell>
                        <TableCell>{item.subordinate_department || "-"}</TableCell>
                        <TableCell>{item.subordinate_mobile || "-"}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenCamera(item)}
                              sx={{
                                backgroundColor: "error.main",
                                color: "white",
                                "&:hover": { backgroundColor: "error.dark" },
                              }}
                            >
                              <AddAPhotoIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenMoreDetails(item)}
                              sx={{
                                backgroundColor: "primary.main",
                                color: "white",
                                "&:hover": { backgroundColor: "primary.dark" },
                              }}
                              title="View Details"
                            >
                              <MoreIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          ) : (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <Typography variant="body1" color="text.secondary">
                No Subordinate Details Found
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            onClick={() => setSubordinateDialogOpen(false)}
            variant="contained"
            color="primary"
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeTable;
